if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      this.variantIdInput = this.form.querySelector('[name=id]');
      this.variantIdInput.disabled = false;
      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton.querySelector('span');
      if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');
      this.collectiveVariants = JSON.parse(this.dataset.collectiveVariants || '{}');
      this.addToCartText = this.dataset.addToCart || 'Add to cart';
      this.soldOutText = this.dataset.soldOut || 'Sold out';

      // On initial page load, check if the default variant is Collective-linked
      this.updateCollectiveAvailability();

      // Dawn's updateVariantInput() sets input.value then dispatches a native
      // "change" event (bubbles:true) on this exact input BEFORE renderProductInfo()
      // starts its fetch.  We catch that here and install a fresh one-shot
      // MutationObserver each time so we always run AFTER Dawn replaces the price.
      this.variantIdInput.addEventListener('change', () => this._onVariantInputChange());
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      this.handleErrorMessage();

      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      this.querySelector('.loading-overlay__spinner').classList.remove('hidden');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const formData = new FormData(this.form);

      // If the selected variant is linked to a Shopify Collective variant, swap the ID
      // so the order routes to the Collective supplier instead of local inventory.
      const collectiveId = this.collectiveVariants[formData.get('id')];
      if (collectiveId) formData.set('id', collectiveId);

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(`${routes.cart_add_url}`, config)
        .then((response) => response.json())
        .then((response) => {
          if (response.status) {
            this.handleErrorMessage(response.description);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            if (!soldOutMessage) return;
            this.submitButton.setAttribute('aria-disabled', true);
            this.submitButton.querySelector('span').classList.add('hidden');
            soldOutMessage.classList.remove('hidden');
            this.error = true;
            return;
          } else if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }

          this.error = false;
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener('modalClosed', () => {
              setTimeout(() => { this.cart.renderContents(response) });
            }, { once: true });
            quickAddModal.hide(true);
          } else {
            this.cart.renderContents(response);
          }
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
          if (!this.error) this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading-overlay__spinner').classList.add('hidden');
        });
    }

    updateCollectiveAvailability() {
      const collectiveId = this.collectiveVariants[this.variantIdInput.value];

      // Not a Collective-linked variant — Dawn handles price and button normally
      if (!collectiveId) {
        this.submitButton.removeAttribute('data-collective-checking');
        return;
      }

      // Mark as checking so a fast variant switch doesn't apply a stale fetch result
      this.submitButton.setAttribute('data-collective-checking', '');

      fetch(`${this.dataset.shopUrl}/variants/${collectiveId}.js`)
        .then((r) => r.json())
        .then(({ available, price, compare_at_price }) => {
          // Bail if the variant changed again while we were fetching
          if (!this.submitButton.hasAttribute('data-collective-checking')) return;
          this.submitButton.removeAttribute('data-collective-checking');

          if (available) {
            this.submitButton.removeAttribute('disabled');
            this.submitButton.removeAttribute('aria-disabled');
            if (this.submitButtonText) this.submitButtonText.textContent = this.addToCartText;
          } else {
            this.submitButton.setAttribute('disabled', '');
            if (this.submitButtonText) this.submitButtonText.textContent = this.soldOutText;
          }

          this.updatePriceDisplay(price, compare_at_price, available);
        })
        .catch(() => {
          // Fail open — let the cart API surface any real error
          this.submitButton.removeAttribute('data-collective-checking');
        });
    }

    // Called every time Dawn fires a "change" event on the hidden id input
    // (inside updateVariantInput), which happens synchronously BEFORE
    // renderProductInfo() starts its async fetch.
    _onVariantInputChange() {
      const variantId    = this.variantIdInput.value;
      const collectiveId = this.collectiveVariants[variantId];
      if (!collectiveId) return; // not Collective-linked — Dawn handles it normally

      const priceWrapper = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceWrapper) return;

      // One-shot observer: fires once when renderProductInfo() replaces
      // priceWrapper.innerHTML, then immediately disconnects.
      const obs = new MutationObserver(() => {
        obs.disconnect(); // one-shot — never fires again from our own DOM writes

        // Guard: user switched variants again while renderProductInfo was in-flight
        if (this.variantIdInput.value !== variantId) return;

        fetch(`${this.dataset.shopUrl}/variants/${collectiveId}.js`)
          .then((r) => r.json())
          .then(({ available, price, compare_at_price }) => {
            if (this.variantIdInput.value !== variantId) return; // stale fetch guard
            this.updatePriceDisplay(price, compare_at_price, available);
            if (available) {
              this.submitButton.removeAttribute('disabled');
              this.submitButton.removeAttribute('aria-disabled');
              if (this.submitButtonText) this.submitButtonText.textContent = this.addToCartText;
            } else {
              this.submitButton.setAttribute('disabled', '');
              if (this.submitButtonText) this.submitButtonText.textContent = this.soldOutText;
            }
          })
          .catch(() => {});
      });

      obs.observe(priceWrapper, { childList: true, subtree: true });
    }

    updatePriceDisplay(price, compareAtPrice, available) {
      const priceWrapper = document.getElementById(`price-${this.dataset.sectionId}`);
      if (!priceWrapper) return;

      const priceEl       = priceWrapper.querySelector('.price');
      const regularBlock  = priceWrapper.querySelector('.price__regular');
      const saleBlock     = priceWrapper.querySelector('.price__sale');
      // Regular price span (inside .price__regular)
      const regularPrice  = regularBlock && regularBlock.querySelector('.price-item--regular');
      // Sale price span (inside .price__sale)
      const salePrice     = saleBlock && saleBlock.querySelector('.price-item--sale');
      // Compare-at strikethrough (inside .price__sale)
      const comparePrice  = saleBlock && saleBlock.querySelector('s.price-item--regular');
      const saleBadge     = priceWrapper.querySelector('.price__badge-sale');

      const isOnSale = compareAtPrice && compareAtPrice > price;

      if (priceEl) {
        priceEl.classList.toggle('price--on-sale', isOnSale);
        priceEl.classList.toggle('price--sold-out', !available);
      }

      if (isOnSale) {
        if (regularPrice) regularPrice.textContent = this.formatMoney(price);
        if (salePrice)    salePrice.textContent    = this.formatMoney(price);
        if (comparePrice) comparePrice.textContent = this.formatMoney(compareAtPrice);
        if (saleBadge) {
          const savingsPct = Math.round((compareAtPrice - price) / compareAtPrice * 100);
          saleBadge.textContent = `${savingsPct}% OFF RETAIL`;
        }
      } else {
        if (regularPrice) regularPrice.textContent = this.formatMoney(price);
        if (salePrice)    salePrice.textContent    = this.formatMoney(price);
        if (saleBadge)    saleBadge.textContent    = '';
      }
    }

    formatMoney(cents) {
      const fmt    = this.dataset.moneyFormat || '${{amount}}';
      const amount = (cents / 100).toFixed(2);
      const noDecimals = String(Math.round(cents / 100));
      const withComma  = amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const noDecComma = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return fmt
        .replace('{{amount_no_decimals_with_comma_separator}}', noDecComma)
        .replace('{{amount_with_comma_separator}}', withComma)
        .replace('{{amount_no_decimals}}', noDecimals)
        .replace('{{amount}}', amount);
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      if (!this.errorMessageWrapper) return;
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }
  });
}
